// Presentation vocabulary for evidence & assurance (APP-004 R3 UI-P4).
//
// Every state here is compliance-relevant: draft evidence must not read as
// approved, expired and superseded must not read as live, and refuting evidence
// must be visibly refuting. The backend names the state; this module only
// decides how it looks.
import { Badge } from "@odingard/design-system";
import type { BadgeTone } from "@odingard/design-system";
import type {
  EvidenceHealthKind,
  EvidenceLinkRole,
  EvidenceLinkTarget,
  EvidenceRequestStatus,
  EvidenceSensitivity,
  EvidenceSource,
  EvidenceStatus,
} from "@odingard/sdk-client";

const STATUS: Record<EvidenceStatus, { label: string; tone: BadgeTone }> = {
  draft: { label: "Draft", tone: "neutral" },
  submitted: { label: "Submitted", tone: "info" },
  approved: { label: "Approved", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  expired: { label: "Expired", tone: "warning" },
  superseded: { label: "Superseded", tone: "neutral" },
};

export function EvidenceStatusBadge({ status }: { status: EvidenceStatus }) {
  const it = STATUS[status];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

export function evidenceStatusLabel(status: EvidenceStatus): string {
  return STATUS[status].label;
}

const REQUEST_STATUS: Record<EvidenceRequestStatus, { label: string; tone: BadgeTone }> = {
  open: { label: "Open", tone: "info" },
  submitted: { label: "Submitted", tone: "info" },
  accepted: { label: "Accepted", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

export function RequestStatusBadge({ status }: { status: EvidenceRequestStatus }) {
  const it = REQUEST_STATUS[status];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

const SENSITIVITY: Record<EvidenceSensitivity, BadgeTone> = {
  public: "neutral",
  internal: "info",
  confidential: "warning",
  restricted: "danger",
};

export function SensitivityBadge({ sensitivity }: { sensitivity: EvidenceSensitivity }) {
  return <Badge tone={SENSITIVITY[sensitivity] ?? "neutral"}>{sensitivity}</Badge>;
}

const SOURCE: Record<EvidenceSource, string> = {
  upload: "Documents record",
  url: "External URL",
  attestation: "Attestation",
  system_export: "System export",
  policy_artifact: "Policy artifact",
};

export function sourceLabel(source: EvidenceSource): string {
  return SOURCE[source] ?? source;
}

export const EVIDENCE_SOURCES: EvidenceSource[] = [
  "upload",
  "url",
  "attestation",
  "system_export",
  "policy_artifact",
];

export const EVIDENCE_SENSITIVITIES: EvidenceSensitivity[] = [
  "public",
  "internal",
  "confidential",
  "restricted",
];

const ROLE: Record<EvidenceLinkRole, { label: string; tone: BadgeTone }> = {
  supports: { label: "Supports", tone: "success" },
  refutes: { label: "Refutes", tone: "danger" },
  context: { label: "Context", tone: "neutral" },
};

export function LinkRoleBadge({ role }: { role: EvidenceLinkRole }) {
  const it = ROLE[role];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

export const EVIDENCE_LINK_ROLES: EvidenceLinkRole[] = ["supports", "refutes", "context"];

const TARGET: Record<EvidenceLinkTarget, string> = {
  requirement: "Requirement",
  mapping: "Mapping",
  policy: "Policy",
  policy_version: "Policy version",
  exception: "Exception",
  compensating_control: "Compensating control",
  audit_project: "Audit project",
  finding: "Finding",
};

export function targetLabel(target: EvidenceLinkTarget): string {
  return TARGET[target] ?? target;
}

export const EVIDENCE_LINK_TARGETS: EvidenceLinkTarget[] = [
  "requirement",
  "mapping",
  "policy",
  "policy_version",
  "exception",
  "compensating_control",
  "audit_project",
  "finding",
];

const HEALTH: Record<EvidenceHealthKind, { label: string; tone: BadgeTone; description: string }> = {
  missing: {
    label: "Missing evidence",
    tone: "danger",
    description: "Requirements with no approved supporting evidence inside its window.",
  },
  stale: {
    label: "Stale evidence",
    tone: "warning",
    description: "Approved evidence past its validity window or review date.",
  },
  duplicate: {
    label: "Duplicate evidence",
    tone: "info",
    description: "The same content hash captured on more than one evidence item.",
  },
  conflicting: {
    label: "Conflicting evidence",
    tone: "danger",
    description: "A target carrying both supporting and refuting evidence.",
  },
};

export function healthLabel(kind: EvidenceHealthKind): string {
  return HEALTH[kind].label;
}

export function healthTone(kind: EvidenceHealthKind): BadgeTone {
  return HEALTH[kind].tone;
}

export function healthDescription(kind: EvidenceHealthKind): string {
  return HEALTH[kind].description;
}

export const EVIDENCE_HEALTH_KINDS: EvidenceHealthKind[] = [
  "missing",
  "stale",
  "duplicate",
  "conflicting",
];

/** Overdue is derived from the backend's due date only — nothing else. */
export function isOverdue(dueAt: string | null, status: EvidenceRequestStatus): boolean {
  if (dueAt === null) return false;
  if (status !== "open" && status !== "submitted") return false;
  return new Date(dueAt).getTime() < Date.now();
}
